import React, { useState } from 'react';
import useData from '../../hooks/useData';
import useToast from '../../hooks/useToast';
import { callGeminiAPI } from '../../services/api';
import { SparklesIcon, DuplicateIcon } from '../ui/Icons'; // Ícone de duplicar/copiar adicionado
import FormattedSuggestion from './FormattedSuggestion';
import GlassButton from '../ui/Button';

const AIAssistant = ({ task }) => {
    const { currentUserProfile } = useData();
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState('plano');
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState({ plano: '', analise: '', comunicacao: '' });
    
    const [communicationInput, setCommunicationInput] = useState('');

    const handleCopyToClipboard = (text) => {
        if (!text) return;
        const plainText = text.replace(/\*\*/g, '');
        navigator.clipboard.writeText(plainText).then(() => {
            showToast('Texto copiado para a área de transferência!');
        }, (err) => {
            showToast('Falha ao copiar o texto.', 'error');
            console.error('Não foi possível copiar o texto: ', err);
        });
    };

    const generatePrompt = (action) => {
        const context = `
            Contexto da Tarefa:
            - Título: "${task.title}"
            - Descrição: "${task.description || 'Nenhuma'}"
            - Cliente: ${task.client || 'Não especificado'}
            - Projeto Vinculado: ${task.projectId || 'Não especificado'}
            - Designer Responsável: ${currentUserProfile?.username || 'Não especificado'}
        `;

        switch (action) {
            case 'plano':
                return `
                    **Persona:** Você é um Gerente de Projetos Sênior em nossa agência. Estamos em uma reunião de kick-off com o designer "${currentUserProfile?.username}" para definir os próximos passos desta tarefa.

                    **Contexto da Tarefa:** ${context}

                    **Instruções:**
                    1. Fale diretamente com o designer (${currentUserProfile?.username}), usando "você".
                    2. Comece a conversa de forma proativa, como "Ok, ${currentUserProfile?.username}, vamos traçar um plano de ação para essa tarefa."
                    3. Crie uma lista de 3 a 4 passos práticos e claros que você recomenda para iniciar e desenvolver o trabalho.
                    4. Mantenha um tom de liderança e mentoria, focado em destravar o projeto.
                    5. NÃO gere um documento formal. A resposta deve ser como um diálogo em uma reunião.
                    6. Responda em português do Brasil.
                `;
            case 'analise':
                return `
                    **Persona:** Você é um Diretor de Criação experiente. Você está em uma reunião com o designer "${currentUserProfile?.username}" para analisar criticamente o briefing desta tarefa.

                    **Contexto da Tarefa:** ${context}

                    **Instruções:**
                    1. Fale diretamente com o designer (${currentUserProfile?.username}), usando "você".
                    2. Comece a análise como se estivessem conversando, por exemplo: "Beleza, ${currentUserProfile?.username}, li o material aqui. Tenho alguns pontos que valem a nossa atenção antes de começar."
                    3. Identifique 2 ou 3 possíveis desafios, inconsistências ou oportunidades escondidas no briefing (em "Pontos de Atenção").
                    4. Formule 1 ou 2 perguntas-chave que o designer deveria fazer a si mesmo ou ao cliente para clarificar o escopo ("Perguntas para Desbloquear").
                    5. O tom deve ser de um mentor experiente, ajudando a prever problemas.
                    6. Responda em português do Brasil.
                `;
            
            case 'comunicacao':
                // --- INÍCIO DA ALTERAÇÃO ---
                return `
                    **Persona:** Você é um especialista em comunicação com clientes, com um talento para criar mensagens que soam humanas, claras e amigáveis para canais como o WhatsApp.

                    **Contexto da Tarefa:** ${context}

                    **Mensagem original do designer:** "${communicationInput}"

                    **Sua Tarefa:** Reescreva a "Mensagem original do designer" para ser enviada a um cliente via WhatsApp.

                    **Instruções Cruciais:**
                    1.  **Soe como um humano:** Evite jargões corporativos e frases clichês. Use uma linguagem natural e positiva. Pense em como um parceiro de negócios prestativo falaria.
                    2.  **Tom:** Profissional, mas amigável e leve. Transmita confiança e proatividade.
                    3.  **Clareza e Concisão:** Mantenha a intenção original da mensagem, mas melhore a clareza. A mensagem deve ser curta e direta.
                    4.  **Generalização:** **NÃO mencione o nome do projeto ou detalhes muito específicos da tarefa.** A mensagem deve ser genérica o suficiente para ser adaptada, focando apenas no texto que o designer forneceu.
                    5.  **Emojis:** Use emojis com extrema moderação (um, no máximo) e apenas se for essencial para o tom da mensagem.
                    6.  **Retorno:** Devolva APENAS o texto final da mensagem, pronto para ser copiado. Não inclua saudações para mim ou explicações.
                    7.  **Língua:** Responda em português do Brasil.
                `;
                // --- FIM DA ALTERAÇÃO ---
            default:
                return '';
        }
    };

    const handleGenerate = async (action) => {
        if (action === 'comunicacao' && !communicationInput.trim()) {
            showToast("Por favor, digite o texto que deseja refinar.", "error");
            return;
        }
        
        const prompt = generatePrompt(action);
        if (!prompt) return;

        setIsLoading(true);
        
        try {
            const apiResult = await callGeminiAPI(prompt);
            setResults(prev => ({ ...prev, [action]: apiResult || "Não foi possível gerar uma resposta." }));
        } catch (error) {
            setResults(prev => ({ ...prev, [action]: "Ocorreu um erro ao contatar a IA. Tente novamente." }));
        } finally {
            setIsLoading(false);
        }
    };
    
    const tabs = [
        { id: 'plano', label: 'Plano de Ação', buttonText: 'Traçar Plano' },
        { id: 'analise', label: 'Análise Crítica', buttonText: 'Analisar Briefing' },
        { id: 'comunicacao', label: 'Refinar Mensagem', buttonText: 'Refinar para WhatsApp' }
    ];

    return (
        <div className="bg-white/5 rounded-lg p-4 mt-4">
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                    <SparklesIcon className="text-indigo-400" />
                    <h3 className="text-lg font-bold text-white">Assistente IA</h3>
                </div>
            </div>
            
            <div className="flex gap-1 rounded-lg bg-black/20 p-1 mb-4">
                {tabs.map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-1 py-2 text-sm rounded-md transition-colors ${activeTab === tab.id ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                        {tab.label}
                    </button>
                ))}
            </div>
            
            <div className="bg-black/20 p-4 rounded-lg min-h-[200px] text-gray-300 flex flex-col justify-center">
                {activeTab === 'comunicacao' ? (
                    <div className="space-y-6">
                        {isLoading && (
                            <p className="animate-pulse text-center">Ajustando a comunicação...</p>
                        )}

                        {results.comunicacao && !isLoading && (
                            <div className="bg-black/20 p-4 rounded-lg animate-fade-in">
                                <h4 className="text-sm font-bold text-indigo-300 mb-2">Sugestão Refinada:</h4>
                                <FormattedSuggestion text={results.comunicacao} />
                                <GlassButton 
                                    onClick={() => handleCopyToClipboard(results.comunicacao)} 
                                    className="!bg-emerald-500/30 hover:!bg-emerald-500/40 mt-4 flex items-center gap-2"
                                >
                                    <DuplicateIcon size={16} />
                                    Copiar Texto
                                </GlassButton>
                            </div>
                        )}

                        <div className="text-center space-y-4">
                            <textarea
                                value={communicationInput}
                                onChange={(e) => setCommunicationInput(e.target.value)}
                                placeholder="Digite o texto que você quer enviar para o cliente..."
                                rows="3"
                                className="w-full p-2 bg-black/20 rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-indigo-400/80 transition-all duration-200 text-white"
                                disabled={isLoading}
                            />
                            <GlassButton 
                                onClick={() => handleGenerate('comunicacao')} 
                                className="!bg-indigo-500 hover:!bg-indigo-600 flex items-center gap-2 mx-auto"
                                disabled={isLoading}
                            >
                                <SparklesIcon size={16} />
                                {results.comunicacao ? 'Refinar Outro Texto' : 'Refinar para WhatsApp'}
                            </GlassButton>
                        </div>
                    </div>
                ) : (
                    isLoading ? (
                        <p className="animate-pulse text-center">Pensando em uma estratégia...</p>
                    ) : (
                        tabs.map(tab => {
                            if (activeTab === tab.id) {
                                if (results[tab.id]) {
                                    return <FormattedSuggestion key={tab.id} text={results[tab.id]} />;
                                }
                                return (
                                    <div key={tab.id} className="text-center animate-fade-in">
                                        <GlassButton onClick={() => handleGenerate(tab.id)} className="!bg-indigo-500 hover:!bg-indigo-600 flex items-center gap-2 mx-auto">
                                            <SparklesIcon size={16} />
                                            {tab.buttonText}
                                        </GlassButton>
                                    </div>
                                );
                            }
                            return null;
                        })
                    )
                )}
            </div>
        </div>
    );
};

export default AIAssistant;